import { z } from "zod";

///wtf is this

/* public
 * -------------------------------------------------------------------------- */

export function createBuilder
  <TRequiredFields extends z.ZodRawShape>
  (requiredFields: TRequiredFields):
  Builder<{ required: TRequiredFields; conditional: {} }> {
  return create(requiredFields);
}


export function mergeBuild
  <TBuilders extends Builder<any>[]>
  (builders: TBuilders):
  z.ZodType<MergeBuilderOutput<TBuilders[number]>> {
  return <any>create(
    Object.assign({}, ...builders.map(b => (b as InternalBuilder<any>)._requiredFields)),
    builders.flatMap(b => (b as InternalBuilder<any>)._conditionClauses),
  ).build() as any;
}


/* internal types
 * -------------------------------------------------------------------------- */

type Builder<TFields extends AnyBuilderFields> = {
  when: <TConditionalFields extends z.ZodRawShape>(
    condition: (values: z.output<z.ZodObject<TFields["required"]>>) => boolean,
    fields: TConditionalFields
  ) => Builder<{
    required: TFields["required"];
    conditional: TFields["conditional"] & TConditionalFields;
  }>;

  build: () => z.ZodType<
    & z.output<z.ZodObject<TFields["required"]>>
    & {
      [k in keyof TFields["conditional"]]?: z.output<TFields["conditional"][k]>
    }
  >;
};


type InternalBuilder<TFields extends AnyBuilderFields> = (
  Builder<TFields> & {
    _requiredFields: TFields["required"];
    _conditionClauses: AnyConditionClause[];
  }
);


type AnyBuilderFields = {
  required: z.ZodRawShape;
  conditional: z.ZodRawShape;
};


type AnyConditionClause = {
  condition: (values: any) => boolean;
  fields: z.ZodRawShape;
};


type BuilderOutput<TBuilder extends Builder<any>> =
  ReturnType<TBuilder["build"]> extends z.ZodType<infer TOutput> ? TOutput : never;


type MergeBuilderOutput<TBuilders extends Builder<any>> =
  UnionToIntersection<BuilderOutput<TBuilders>>;


type UnionToIntersection<U> =
  (U extends any ? (k: U) => void : never) extends
  ((k: infer I) => void) ? I : never;


/* internal implementation
 * -------------------------------------------------------------------------- */

function create
  <TRequiredFields extends z.ZodRawShape>
  (requiredFields: TRequiredFields, conditionClauses: AnyConditionClause[] = []):
  Builder<{ required: TRequiredFields; conditional: {} }> {
  return <InternalBuilder<any>>{
    _requiredFields: { ...requiredFields },
    _conditionClauses: conditionClauses,

    when(condition, fields) {
      return create(
        { ...requiredFields },
        [...conditionClauses, { condition, fields }]
      );
    },

    build() {
      const baseSchema = z.looseObject(requiredFields);
      const refinedSchema = baseSchema.check(ctx => {
        for (const { condition, fields } of conditionClauses) {
          if (condition(ctx.value)) {
            for (const [key, schema] of Object.entries(fields) as [string, z.ZodType<unknown>][]) {
              const value = (ctx.value as Record<string, unknown>)[key];
              // const result =
              schema.parseAsync(value).then((value) => {
                console.log({ value })
              }).catch((error) => {
                ctx.issues.push({
                  path: [key],
                  code: "custom",
                  message: error.message,
                  input: ctx.value,
                });
              });
              // if (!result.success) {
              //   ctx.issues.push({
              //     path: [key],
              //     code: "custom",
              //     message: result.error.message,
              //     input: ctx.value,
              //   });
              // }
            }
          }
        }
      });

      const outputSchema = refinedSchema.transform(values => {
        const requiredKeys = Object.keys(requiredFields) as (keyof z.output<typeof baseSchema>)[];
        const requiredValues = selectKeys(values, requiredKeys);
        const conditionalValues = Object.assign(
          {},
          ...conditionClauses
            .filter(({ condition }) => condition(requiredValues))
            .map(({ fields }) => Object.keys(fields))
            .map(keys => selectKeys(values, keys as (keyof typeof values)[]))
        );

        return {
          ...requiredValues,
          ...conditionalValues,
        };
      });

      return outputSchema;
    }
  };
}


/* util
 * -------------------------------------------------------------------------- */

function selectKeys<
  TObject extends Record<string, any>,
  TKeys extends readonly (keyof TObject)[]
>(
  object: TObject,
  keys: TKeys
): Pick<TObject, TKeys[number]> {
  const keySet = new Set(keys);
  return Object.fromEntries(
    Object.entries(object).filter(([key]) =>
      keySet.has(key as keyof TObject)
    )
  ) as Pick<TObject, TKeys[number]>;
}
